<template>
  <div class="overflow-hidden">
    <Headline class="mb-4" tag="span" look="h3">
      {{ $t('pdp.product_detail_specs') }}
    </Headline>
    <ul class="-mb-1 grid gap-x-8 text-sm" :class="gridClasses">
      <li
        v-for="entry in productProperties"
        :key="entry.label"
        class="flex justify-between border-b border-neutral-40 py-2.5 leading-[18px]"
      >
        <div>
          <span class="font-medium">
            {{ entry.label }}
          </span>
          <AsyncTooltip
            v-if="entry.tooltip"
            :text="entry.tooltip"
            class="!absolute text-base text-neutral-50"
          >
            <LazyIconFielmannInfoCircle class="mb-2 ml-2 inline size-4" />
          </AsyncTooltip>
        </div>
        <span class="text-balance pl-2.5 text-right">{{ entry.value }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import {
  getFirstAttributeValue,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'
import Headline from '~/components/ui/headlines/Headline.vue'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  detailProperties: {
    type: Array as PropType<string[]>,
    required: true,
  },
  activeVariant: {
    type: [Object, null] as PropType<Variant | null>,
    default: null,
  },
  splitBy: {
    type: Number as PropType<number>,
    validator: (value: number) => [1, 3].includes(value),
    default: 1,
  },
  translations: {
    type: Object as PropType<{ [key: string]: string }>,
    default: () => ({}),
  },
})

const $i18n = useI18n()

const productProperties = computed(() => {
  const obj: Record<string, any> = props.detailProperties
    .map((key) => {
      const firstValue =
        getFirstAttributeValue(props.product.attributes, key) ||
        getFirstAttributeValue(props.activeVariant?.attributes, key)

      const allValues =
        getAttributeValueTuples(props.product.attributes, key) ||
        getAttributeValueTuples(props.activeVariant?.attributes, key)
      if (allValues.length > 1) {
        // mimic structure of single value
        return {
          label: allValues.map((entry) => entry.label).join(', '),
        }
      }
      return firstValue
    })
    .reduce((obj, attribute, index) => {
      const productPropertyKey = props.detailProperties[index]
      const label =
        props.translations?.[productPropertyKey] ||
        props.product.attributes?.[productPropertyKey]?.label

      return label
        ? {
            ...obj,
            [productPropertyKey]: {
              label,
              value: attribute?.label,
            },
          }
        : { ...obj }
    }, {})

  if (isGlasses(props.product) && props.product.attributes?.filterCategory) {
    const value = getFirstAttributeValue(
      props.product.attributes,
      'filterCategory',
    )?.value

    obj.filterCategory = {
      label: $i18n.t('pdp.details.filterCategory.label'),
      value,
      tooltip: $i18n.t(`pdp.details.filterCategory.cat${value}_text`),
    }
  }

  if (props.product.variants && !isCareProducts(props.product)) {
    obj.referenceKey = {
      value: props.activeVariant?.referenceKey,
      label: $i18n.t('pdp.article_number'),
    }
  }

  if (obj.lensAttributesFilter || obj.lensAttributesShade) {
    const properties = [
      ...new Set(
        [
          obj.lensAttributesFilter?.value,
          obj.lensAttributesShade?.value,
        ].filter((prop) => prop),
      ),
    ]

    obj.glassesProperties = {
      value: properties.join(', '),
      label: $i18n.t('pdp.details.glassesProperties'),
    }

    delete obj.lensAttributesFilter
    delete obj.lensAttributesShade
  }

  return obj
})

const countPerPart = computed(() => {
  return Math.ceil(Object.keys(productProperties.value).length / props.splitBy)
})

// Check tailwind config safelist for grid-rows more than 8
const gridClasses = computed(() => ({
  'col-span-2 md:col-span-12': props.splitBy === 1,
  [`md:grid-rows-${countPerPart.value} col-span-2 md:col-span-4 md:grid-flow-col md:grid-cols-3`]:
    props.splitBy === 3,
}))
</script>
